/* sticky-atc.js — the PDP sticky Add-to-Cart bar
   (snippets/product-sticky-atc.liquid, Figma 344:11073 desktop / 344:11784 mobile).

   The bar owns no product form. Its button is associated with the main product
   form through the `form` attribute, so a click dispatches `submit` on that form
   and Dawn's <product-form> (assets/product-form.js) adds the same variant and
   quantity the page already has selected. Two consequences worth knowing:

   - The capture-phase mounting guard in mounting.js still fires. It listens on the
     <product-form> ancestor, and the propagation path is computed from the FORM's
     position in the tree, not the button's — so a blocked ski cannot be added from
     down here either.
   - product-form.js only knows about the submit button INSIDE itself, so it never
     paints loading/disabled state on ours. Everything visible in this bar is
     therefore mirrored from that main button (label, live total, disabled,
     loading, mounting-blocked), which also means there is one source of truth and
     no second price calculation to drift.

   On init the element reparents itself to <body> so no clipped or transformed
   ancestor can trap its `position: fixed`, then slides in whenever the main
   Add-to-Cart row is fully out of view.
*/

if (!customElements.get('sticky-atc')) {
  customElements.define(
    'sticky-atc',
    class StickyAtc extends HTMLElement {
      connectedCallback() {
        if (this.initialized) return;

        this.mainButton = document.getElementById(this.dataset.submitButton);
        // Without the main button there is nothing to submit and nothing to
        // mirror — stay hidden rather than render a dead bar.
        if (!this.mainButton) return;

        this.initialized = true;
        this.cta = this.querySelector('[data-sticky-atc-cta]');
        this.labelElement = this.querySelector('[data-sticky-atc-label]');
        this.totalElement = this.querySelector('[data-sticky-atc-total]');
        this.thumb = this.querySelector('[data-sticky-atc-thumb]');
        this.spinner = this.querySelector('.loading__spinner');
        this.cta?.addEventListener('click', (event) => {
          if (this.cta.type !== 'button') return;
          event.preventDefault();
          const mounting = this.mountingControl;
          if (mounting?.canGuide) mounting.revealMissingSoleLength();
        });

        // A theme-editor section reload re-renders <product-info> but leaves the
        // previous bar parented to <body>. Clear it before adding this one.
        document.querySelectorAll(`body > sticky-atc[data-section="${this.dataset.section}"]`).forEach((bar) => {
          if (bar !== this) bar.remove();
        });

        // appendChild removes and re-inserts us, which fires
        // disconnectedCallback/connectedCallback synchronously — `initialized`
        // makes the re-entry a no-op and `moving` keeps the teardown from firing.
        if (this.parentElement !== document.body) {
          this.moving = true;
          document.body.appendChild(this);
          this.moving = false;
        }

        this.removeAttribute('hidden');
        this.sync();

        // Threshold 0: the bar shows only while the main row is FULLY out of
        // view. On desktop the info column is sticky (.product__column-sticky),
        // so that happens once the gallery has scrolled past.
        this.observer = new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            const visible = !entry.isIntersecting;
            this.classList.toggle('is-visible', visible);
            // Pad the page bottom by the bar's height only while it is shown — a
            // standing padding leaves dead space below the footer when parked.
            document.body.classList.toggle('sticky-atc-enabled', visible);
          });
        });
        this.observer.observe(this.mainButton);

        this.resizeObserver = new ResizeObserver(() => {
          document.documentElement.style.setProperty('--sticky-atc-height', `${this.clientHeight}px`);
        });
        this.resizeObserver.observe(this);

        // Dawn rewrites the main button's attributes (product-info.js
        // toggleSubmitButton), its label text, and the total's text (pdp.js) —
        // all of which land as mutations somewhere in its subtree.
        this.mutationObserver = new MutationObserver(() => this.sync());
        this.mutationObserver.observe(this.mainButton, {
          attributes: true,
          attributeFilter: ['disabled', 'aria-disabled', 'class'],
          childList: true,
          subtree: true,
          characterData: true,
        });

        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          this.unsubscribeVariant = subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
            if (data?.sectionId !== this.dataset.section) return;
            this.updateThumb(data.html);
            this.sync();
          });
        }
      }

      disconnectedCallback() {
        if (this.moving) return;
        this.observer?.disconnect();
        this.resizeObserver?.disconnect();
        this.mutationObserver?.disconnect();
        this.unsubscribeVariant?.();
        document.body.classList.remove('sticky-atc-enabled');
        document.documentElement.style.removeProperty('--sticky-atc-height');
      }

      /* Mirrors everything visible from the main submit button. */
      sync() {
        if (!this.cta || !this.mainButton) return;

        // The main button holds the label in a bare <span> and, when the block's
        // "show total price" setting is on, the running total in a second one.
        const label = this.mainButton.querySelector(':scope > span:not([class])');
        if (label && this.labelElement) this.labelElement.textContent = label.textContent.trim();

        const total = this.mainButton.querySelector('[data-pdp-total]');
        const disabled = this.mainButton.hasAttribute('disabled');
        if (this.totalElement) {
          if (total) this.totalElement.textContent = total.textContent.trim();
          // A sold-out variant has no price to advertise — same rule as the main
          // button (assets/section-pdp.css hides .pdp-submit__total when disabled).
          this.totalElement.hidden = disabled || !total;
        }

        const loading = this.mainButton.classList.contains('loading');
        const unavailable = this.mainButton.getAttribute('aria-disabled') === 'true';
        const guide = this.mainButton.classList.contains('pdp-submit--blocked') && !disabled && !loading && !unavailable;
        this.cta.disabled = disabled || loading;
        // The incomplete state explicitly takes the shopper to the sole-length
        // field. It never submits the form; the normal submit path returns once
        // mounting is answered, using the same form and line properties.
        this.cta.type = guide ? 'button' : 'submit';
        if (guide && this.labelElement && this.dataset.completeLabel) {
          this.labelElement.textContent = this.dataset.completeLabel;
        }
        const inputId = this.mountingControl?.bslInput?.id;
        if (guide && inputId) this.cta.setAttribute('aria-controls', inputId);
        else this.cta.removeAttribute('aria-controls');
        this.cta.classList.toggle('loading', loading);
        this.spinner?.classList.toggle('hidden', !loading);
        this.cta.classList.toggle('sticky-atc__cta--blocked', guide);
        if (unavailable) {
          this.cta.setAttribute('aria-disabled', 'true');
        } else {
          this.cta.removeAttribute('aria-disabled');
        }
      }

      get mountingControl() {
        return Array.from(document.querySelectorAll('pdp-mounting'))
          .find((mounting) => mounting.querySelector('[data-bsl-input]')?.form === this.cta?.form);
      }

      /* The thumbnail follows the variant. `html` is the freshly fetched section
         document product-info.js publishes with variantChange, so the swap uses
         exactly what the server would have rendered — no client-side image URL
         building. */
      updateThumb(html) {
        if (!html || !this.thumb) return;
        const source = html.querySelector('sticky-atc [data-sticky-atc-thumb]');
        if (source) this.thumb.innerHTML = source.innerHTML;
      }
    }
  );
}
